import './index.css'
import cn from 'clsx'

import React, { Children, cloneElement } from 'react'
export type Props = {
  className?: string
  children?: React.ReactElement<any>[]
  inputClassName?: string
  labelClassName?: string
  disabled?: boolean
  value?: any
  onChange?: (...args: Array<any>) => any
}

/**
 * @deprecated Use `RadioGroup` from `components/ds` instead.
 * @see {@link components/ds/RadioGroup}
 * @migration
 * - Replace legacy RadioGroup with DS RadioGroup compound component:
 *   ```tsx
 *   // Legacy
 *   <RadioGroup value={value} onChange={onChange}>
 *     <RadioButton value="1" label="Option 1" />
 *     <RadioButton value="2" label="Option 2" />
 *   </RadioGroup>
 *
 *   // DS alternative
 *   <RadioGroup.Root value={value} onValueChange={onChange}>
 *     <RadioGroup.Item value="1">Option 1</RadioGroup.Item>
 *     <RadioGroup.Item value="2">Option 2</RadioGroup.Item>
 *   </RadioGroup.Root>
 *   ```
 * - Prop mapping:
 *   - value → value (same)
 *   - onChange(value) → onValueChange(value)
 *   - disabled → disabled (same)
 *   - className → Use Radix layout components or className on wrapper
 * - inputClassName/labelClassName have no DS equivalent; use Radix theme variants
 *
 * A radio group component that manages a collection of radio buttons with shared state
 * @param className - Additional CSS classes for the container div
 * @param children - Array of RadioButton components to manage
 * @param inputClassName - CSS classes to apply to all radio button inputs
 * @param labelClassName - CSS classes to apply to all radio button labels
 * @param disabled - Whether to disable all radio buttons in the group (default: false)
 * @param value - Currently selected value in the radio group
 * @param onChange - Callback fired when any radio button selection changes
 * @returns Container div with radio buttons that have synchronized checked state and change handlers
 */
const RadioGroup = (props: Props): React.ReactElement<'div'> => {
  const {
    className,
    inputClassName,
    labelClassName,
    children,
    disabled,
    value,
    onChange,
  } = props
  return (
    <div className={className}>
      {Children.map(children, radio => {
        if (!radio) return radio

        return cloneElement(radio, {
          ...radio.props,
          ...{
            checked: radio.props.value === value,
            disabled: radio.props.disabled || disabled,
            onChange: () => onChange && onChange(radio.props.value),
            inputClassName: cn(inputClassName, radio.props.inputClassName),
            labelClassName: cn(labelClassName, radio.props.labelClassName),
          },
        })
      })}
    </div>
  )
}

RadioGroup.defaultProps = {
  disabled: false,
}
export default RadioGroup
